import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import PlayerSetup from '../common/PlayerSetup';
import PassAndPlay from '../common/PassAndPlay';
import CategoryChips from '../common/CategoryChips';
import {AssociationItem, BASE_WORDS, CUSTOM_CATEGORY} from './associationsWords';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_WORDS_KEY = 'associations_custom_words';
const DISABLED_CATEGORIES_KEY = 'associations_disabled_categories';

type Stage = 'setup' | 'collecting' | 'review' | 'guessing' | 'result';

interface ClueEntry {
    playerId: string;
    playerName: string;
    text: string;
    autoStruck: boolean;
}

const normalize = (s: string) => s.trim().toLowerCase();

const AssociationsGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customWords, setCustomWords] = useState<AssociationItem[]>(() => loadJSON(CUSTOM_WORDS_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newWordText, setNewWordText] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [guesserIndex, setGuesserIndex] = useState(0);
    const [currentWord, setCurrentWord] = useState<AssociationItem | null>(null);
    const [usedWords, setUsedWords] = useState<Set<string>>(new Set());
    const [clueDrafts, setClueDrafts] = useState<Record<string, string>>({});
    const [struck, setStruck] = useState<Set<string>>(new Set());
    const [cluesRevealed, setCluesRevealed] = useState(false);
    const [lastResult, setLastResult] = useState<boolean | null>(null);
    const [score, setScore] = useState({correct: 0, total: 0});

    const allWords = useMemo(() => [...BASE_WORDS, ...customWords], [customWords]);
    const categories = useMemo(() => {
        const set = new Set(allWords.map(w => w.category));
        return Array.from(set);
    }, [allWords]);
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        allWords.forEach(w => {
            counts[w.category] = (counts[w.category] || 0) + 1;
        });
        return counts;
    }, [allWords]);
    const activeWords = useMemo(
        () => allWords.filter(w => !disabledCategories.includes(w.category)),
        [allWords, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const addCustomWord = () => {
        const text = newWordText.trim();
        if (!text) return;
        const next = [...customWords, {text, category: CUSTOM_CATEGORY, custom: true}];
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
        setNewWordText('');
    };

    const removeCustomWord = (text: string) => {
        const next = customWords.filter(w => w.text !== text);
        setCustomWords(next);
        saveJSON(CUSTOM_WORDS_KEY, next);
    };

    const pickWord = (pool: AssociationItem[], used: Set<string>): {word: AssociationItem; nextUsed: Set<string>} => {
        let available = pool.filter(w => !used.has(w.text));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const word = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(word.text);
        return {word, nextUsed: updatedUsed};
    };

    const cluePlayers = useMemo(
        () => players.filter((_, i) => i !== guesserIndex),
        [players, guesserIndex]
    );

    const beginRound = (nextGuesserIndex: number, pool: AssociationItem[], used: Set<string>) => {
        const {word, nextUsed} = pickWord(pool, used);
        setGuesserIndex(nextGuesserIndex);
        setCurrentWord(word);
        setUsedWords(nextUsed);
        setClueDrafts({});
        setStruck(new Set());
        setCluesRevealed(false);
        setStage('collecting');
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeWords.length === 0) return;
        setScore({correct: 0, total: 0});
        beginRound(0, activeWords, new Set());
    };

    const nextRound = () => {
        beginRound((guesserIndex + 1) % players.length, activeWords, usedWords);
    };

    const finishCollecting = () => {
        if (!currentWord) return;
        const entries: ClueEntry[] = cluePlayers
            .map(p => ({playerId: p.id, playerName: p.name, text: (clueDrafts[p.id] || '').trim(), autoStruck: false}))
            .filter(e => e.text.length > 0);

        const secretNorm = normalize(currentWord.text);
        const seen = new Map<string, string[]>();
        const autoStruckIds = new Set<string>();

        entries.forEach(e => {
            const norm = normalize(e.text);
            if (norm === secretNorm) {
                autoStruckIds.add(e.playerId);
                return;
            }
            const list = seen.get(norm) || [];
            list.push(e.playerId);
            seen.set(norm, list);
        });
        seen.forEach(list => {
            if (list.length > 1) list.forEach(id => autoStruckIds.add(id));
        });

        setStruck(autoStruckIds);
        setStage('review');
    };

    const toggleStrike = (playerId: string) => {
        setStruck(s => {
            const next = new Set(s);
            if (next.has(playerId)) next.delete(playerId); else next.add(playerId);
            return next;
        });
    };

    const finishGuess = (success: boolean) => {
        setLastResult(success);
        setScore(s => ({correct: s.correct + (success ? 1 : 0), total: s.total + 1}));
        setStage('result');
    };

    const backToSetup = () => {
        setStage('setup');
        setCurrentWord(null);
    };

    const guesser = players[guesserIndex];

    const clueEntries: ClueEntry[] = useMemo(() => {
        if (!currentWord) return [];
        return cluePlayers
            .map(p => ({playerId: p.id, playerName: p.name, text: (clueDrafts[p.id] || '').trim(), autoStruck: false}))
            .filter(e => e.text.length > 0);
    }, [cluePlayers, clueDrafts, currentWord]);

    const survivingClues = clueEntries.filter(e => !struck.has(e.playerId));

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Ассоциации</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Все, кроме отгадывающего, втайне друг от друга придумывают по одному слову-подсказке
                            к общему слову. Одинаковые подсказки вычёркиваются — отгадывающий видит только то,
                            что осталось, и должен назвать слово.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ({activeWords.length} слов активно)</h3>
                            <CategoryChips
                                categories={categories}
                                counts={categoryCounts}
                                disabled={disabledCategories}
                                onToggle={toggleCategory}
                            />

                            {customWords.length > 0 && (
                                <div className="GmList" style={{marginBottom: 12}}>
                                    {customWords.map(w => (
                                        <div className="GmListItem" key={w.text}>
                                            <span className="GmListItem-name">{w.text}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomWord(w.text)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow">
                                <input
                                    className="GmInput"
                                    placeholder="Своё слово"
                                    value={newWordText}
                                    onChange={(e) => setNewWordText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomWord();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomWord}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeWords.length === 0}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'collecting' && currentWord && guesser && (
                    <>
                        <p className="GmHint" style={{marginBottom: 10, textAlign: 'center'}}>
                            {guesser.name} отгадывает — телефон ему(ей) не показываем
                        </p>
                        <PassAndPlay
                            players={cluePlayers}
                            onDone={finishCollecting}
                            renderCard={(player) => (
                                <>
                                    <p className="GmHint">Слово</p>
                                    <p className="GmRoleLocation">{currentWord.text}</p>
                                    <p className="GmHint" style={{marginTop: 14}}>
                                        Придумай одну подсказку — одно слово, без однокоренных и переводов
                                    </p>
                                    <input
                                        className="GmInput"
                                        style={{marginTop: 8}}
                                        placeholder="Твоя подсказка"
                                        value={clueDrafts[player.id] || ''}
                                        onChange={(e) => setClueDrafts(d => ({...d, [player.id]: e.target.value}))}
                                    />
                                </>
                            )}
                        />
                    </>
                )}

                {stage === 'review' && guesser && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Проверьте подсказки</h3>
                        <p className="GmHint GmHint--warn" style={{marginBottom: 12}}>
                            ⚠ {guesser.name} не должен(на) видеть этот экран
                        </p>

                        {clueEntries.length === 0 && (
                            <p className="GmHint">Никто не успел придумать подсказку</p>
                        )}

                        <div className="GmChecklist">
                            {clueEntries.map(entry => (
                                <label className="GmCheckItem" key={entry.playerId}>
                                    <input
                                        type="checkbox"
                                        checked={!struck.has(entry.playerId)}
                                        onChange={() => toggleStrike(entry.playerId)}
                                    />
                                    <span style={struck.has(entry.playerId) ? {textDecoration: 'line-through', color: '#6a6a7a'} : undefined}>
                                        {entry.text}
                                    </span>
                                    <span className="GmHint" style={{marginLeft: 'auto'}}>{entry.playerName}</span>
                                </label>
                            ))}
                        </div>

                        <p className="GmHint">Одинаковые подсказки и подсказки, совпадающие со словом, уже вычеркнуты — снимите или добавьте галочки при необходимости.</p>

                        <div style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('guessing')}>
                                Показать подсказки {guesser.name}
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'guessing' && currentWord && guesser && !cluesRevealed && (
                    <div className="GmPassCard">
                        <p className="GmPassCard-hint">Передайте телефон отгадывающему</p>
                        <h2 className="GmPassCard-name">{guesser.name}</h2>
                        <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setCluesRevealed(true)}>
                            Я — {guesser.name}, посмотреть подсказки
                        </button>
                    </div>
                )}

                {stage === 'guessing' && currentWord && guesser && cluesRevealed && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Подсказки для {guesser.name}</h3>
                        <div className="GmRevealList">
                            {survivingClues.length === 0 && <p className="GmHint">Подсказок не осталось</p>}
                            {survivingClues.map(c => (
                                <div className="GmRevealItem" key={c.playerId}>{c.text}</div>
                            ))}
                        </div>
                        <div className="GmBtnRow" style={{marginTop: 16, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={() => finishGuess(true)}>Угадал ✅</button>
                            <button className="GmBtn" onClick={() => finishGuess(false)}>Не угадал ❌</button>
                        </div>
                    </div>
                )}

                {stage === 'result' && currentWord && (
                    <div className="GmCard" style={{textAlign: 'center'}}>
                        <h3 className="GmCard-title">{lastResult ? 'Угадал! 🎉' : 'Не угадал'}</h3>
                        <p>Слово было: <strong>{currentWord.text}</strong></p>
                        <p className="GmHint">Командный счёт: {score.correct} из {score.total}</p>
                        <div className="GmBtnRow" style={{marginTop: 16, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRound}>Следующее слово</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssociationsGame;
